import { FC, useEffect, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { RxLapTimer } from 'react-icons/rx';
import { MdRefresh } from 'react-icons/md';

import { IEmployee } from '../../Interfaces/interfaces';
import { PulseiraComponent } from '../PulseiraComponent';
import { EventService } from '../../Services/EventService';
import { MyButton } from '../MyButton';
import { z } from 'zod';
import { set, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ErrorMessage } from '../ErrorMessage';
import { Button, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { CompanyService } from '../../Services/CompanyService';
import { Company } from '../../models/eventType';

interface FieldConfig {
    id: string;
    label: string;
    enabled: boolean;
    position: number;
    span: number; // Quantos quadrados o campo ocupa (1, 2, 3 ou 4)
}



interface IProps {
    setShow: React.Dispatch<React.SetStateAction<boolean>>;
    show: boolean;
    reloadPage: Function;
    funcionario: IEmployee | undefined;
    companys: Company[];
}

const schema = z.object({
    choice: z.string().nullable()
})

type FormData = z.infer<typeof schema>

const ModalPrintFuncionario: FC<IProps> = (props) => {

    const service = new EventService();

    const [company, setCompany] = useState<Company>();
    const [confirmModal, setConfirmModal] = useState(false);

    // Carregar configuração do localStorage ou usar padrão
    const loadFieldsConfig = (): FieldConfig[] => {
        const saved = localStorage.getItem('pulseiraFieldsConfig');
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                // Adicionar span padrão se não existir (compatibilidade)
                return parsed.map((field: any) => ({
                    ...field,
                    span: field.span || 1
                }));
            } catch (e) {
                console.error('Erro ao carregar configuração:', e);
            }
        }
        return [
            { id: 'nome', label: 'Nome', enabled: true, position: 1, span: 1 },
            { id: 'empresa', label: 'Empresa', enabled: true, position: 2, span: 1 },
            { id: 'setor', label: 'Setor', enabled: true, position: 3, span: 1 },
            { id: 'funcao', label: 'Função', enabled: true, position: 4, span: 1 }
        ];
    };

    const [fields, setFields] = useState<FieldConfig[]>(loadFieldsConfig());

    // Estados para controlar QR Code e exibição de títulos
    const [showQRCode, setShowQRCode] = useState(() => {
        const saved = localStorage.getItem('pulseiraShowQRCode');
        return saved !== null ? JSON.parse(saved) : true;
    });

    const [showLabels, setShowLabels] = useState(() => {
        const saved = localStorage.getItem('pulseiraShowLabels');
        return saved !== null ? JSON.parse(saved) : true;
    });

    // Estados para controlar tamanho das fontes
    const [labelFontSize, setLabelFontSize] = useState(() => {
        const saved = localStorage.getItem('pulseiraLabelFontSize');
        return saved !== null ? parseInt(saved) : 14;
    });

    const [valueFontSize, setValueFontSize] = useState(() => {
        const saved = localStorage.getItem('pulseiraValueFontSize');
        return saved !== null ? parseInt(saved) : 12;
    });

    const {
        register,
        handleSubmit,
        reset,
        formState: {
            errors
        },
        setValue
    } = useForm<FormData>({
        resolver: zodResolver(schema)
    });

    useEffect(() => {
        let company = props.companys.find(c => c.companyId === props.funcionario?.companyId);
        setCompany(company);
    }, [props.funcionario?.employeeId]);

    // Salvar no localStorage sempre que fields mudar
    useEffect(() => {
        localStorage.setItem('pulseiraFieldsConfig', JSON.stringify(fields));
    }, [fields]);

    // Salvar showQRCode no localStorage
    useEffect(() => {
        localStorage.setItem('pulseiraShowQRCode', JSON.stringify(showQRCode));
    }, [showQRCode]);

    // Salvar showLabels no localStorage
    useEffect(() => {
        localStorage.setItem('pulseiraShowLabels', JSON.stringify(showLabels));
    }, [showLabels]);

    // Salvar labelFontSize no localStorage
    useEffect(() => {
        localStorage.setItem('pulseiraLabelFontSize', labelFontSize.toString());
    }, [labelFontSize]);

    // Salvar valueFontSize no localStorage
    useEffect(() => {
        localStorage.setItem('pulseiraValueFontSize', valueFontSize.toString());
    }, [valueFontSize]);

    const handleClose = () => {
        setConfirmModal(false);
        props.setShow(false);
    };

    const toggleField = (id: string) => {
        setFields(prevFields => {
            const field = prevFields.find(f => f.id === id);
            if (!field) return prevFields;

            if (field.enabled) {
                // Se está desmarcando, apenas desabilita
                return prevFields.map(f =>
                    f.id === id ? { ...f, enabled: false } : f
                );
            } else {
                // Se está marcando, atribui a próxima posição disponível
                const enabledFields = prevFields.filter(f => f.enabled);
                const nextPosition = enabledFields.length + 1;

                return prevFields.map(f =>
                    f.id === id ? { ...f, enabled: true, position: nextPosition, span: 1 } : f
                );
            }
        });
    };

    const changePosition = (id: string, newPosition: number) => {
        setFields(fields.map(field =>
            field.id === id ? { ...field, position: newPosition } : field
        ));
    };

    const changeSpan = (id: string, newSpan: number) => {
        setFields(fields.map(field =>
            field.id === id ? { ...field, span: newSpan } : field
        ));
    };

    // Ordena os campos pela posição antes de passar para o componente
    const getSortedFields = () => {
        return [...fields].sort((a, b) => a.position - b.position);
    };

    // Função para resetar as configurações para o padrão
    const resetFields = () => {
        const defaultFields = [
            { id: 'nome', label: 'Nome', enabled: false, position: 1, span: 1 },
            { id: 'empresa', label: 'Empresa', enabled: false, position: 1, span: 1 },
            { id: 'setor', label: 'Setor', enabled: false, position: 1, span: 1 },
            { id: 'funcao', label: 'Função', enabled: false, position: 1, span: 1 }
        ];
        setFields(defaultFields);
        setShowQRCode(false);
        setShowLabels(false);
        setLabelFontSize(14);
        setValueFontSize(12);
        localStorage.setItem('pulseiraFieldsConfig', JSON.stringify(defaultFields));
        localStorage.setItem('pulseiraShowQRCode', JSON.stringify(false));
        localStorage.setItem('pulseiraShowLabels', JSON.stringify(false));
        localStorage.setItem('pulseiraLabelFontSize', '14');
        localStorage.setItem('pulseiraValueFontSize', '12');
    };

    const handle = async (data: FormData) => {
        if (data.choice === null) {
            toast.error('Selecione se e Checkin ou Checkout por favor')
            return;
        }
        if (!props.funcionario) {
            toast.error('Tente novamente por favor !')
            return;
        }

        reset();
        setValue('choice', null);
        await service.createTimeSheet(props.funcionario, data.choice, handleClose);
    }

    return (
        <>
            <Modal show={props.show} size="lg">
                <Modal.Header closeButton onHide={() => {
                    setConfirmModal(true);
                }} style={{ textAlign: 'center' }}>
                    <Modal.Title style={{ width: '100%' }}>
                        <div style={{ margin: '0px', flexDirection: 'column' }}>
                            <p>Impressão da Pulseira</p>
                            <p style={{ margin: '0px', fontSize: '15px' }}>{props.funcionario?.fullName}</p>
                        </div>
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {/* Checkboxes para controle de exibição e ordenação */}
                    <div className="mb-3 p-2 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="mb-0" style={{ fontSize: '14px' }}>Campos da impressão:</h6>
                            <button
                                type="button"
                                onClick={resetFields}
                                className="btn btn-sm"
                                style={{
                                    backgroundColor: '#003958',
                                    color: 'white',
                                    border: 'none',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    fontSize: '12px'
                                }}
                                title="Restaurar configuração padrão"
                            >
                                <MdRefresh size={16} />
                                Reset
                            </button>
                        </div>
                        <div className="row g-2">
                            {fields.map((field) => (
                                <div key={field.id} className="col-md-6 col-12">
                                    <div className="d-flex align-items-center gap-2 p-2 border rounded" style={{ backgroundColor: 'white' }}>
                                        <input
                                            type="checkbox"
                                            id={`checkbox-${field.id}`}
                                            checked={field.enabled}
                                            onChange={() => toggleField(field.id)}
                                            className="form-check-input m-0"
                                            style={{
                                                cursor: 'pointer',
                                                accentColor: '#003958'
                                            }}
                                        />
                                        <label
                                            htmlFor={`checkbox-${field.id}`}
                                            className="flex-grow-1 mb-0"
                                            style={{ cursor: 'pointer', fontSize: '13px' }}
                                        >
                                            {field.label}
                                        </label>
                                        <div className="d-flex align-items-center gap-1">
                                            <small style={{ fontSize: '11px' }}>Pos:</small>
                                            <Form.Select
                                                size="sm"
                                                style={{ width: '70px', fontSize: '12px' }}
                                                value={field.position}
                                                onChange={(e) => changePosition(field.id, parseInt(e.target.value))}
                                                disabled={!field.enabled}
                                            >
                                                <option value={1}>1 ↖</option>
                                                <option value={2}>2 ↗</option>
                                                <option value={3}>3 ↙</option>
                                                <option value={4}>4 ↘</option>
                                            </Form.Select>
                                        </div>
                                        <div className="d-flex align-items-center gap-1">
                                            <small style={{ fontSize: '11px' }}>Tam:</small>
                                            <Form.Select
                                                size="sm"
                                                style={{ width: '65px', fontSize: '12px' }}
                                                value={field.span}
                                                onChange={(e) => changeSpan(field.id, parseInt(e.target.value))}
                                                disabled={!field.enabled}
                                            >
                                                <option value={1}>1</option>
                                                <option value={2}>2</option>
                                                <option value={3}>3</option>
                                                <option value={4}>4</option>
                                            </Form.Select>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Checkboxes para QR Code e Títulos */}
                        <div className="mt-3 pt-2 border-top">
                            <div className="row g-2">
                                <div className="col-md-6 col-12">
                                    <div className="d-flex align-items-center gap-2 p-2 border rounded" style={{ backgroundColor: 'white' }}>
                                        <input
                                            type="checkbox"
                                            id="checkbox-qrcode"
                                            checked={showQRCode}
                                            onChange={(e) => setShowQRCode(e.target.checked)}
                                            className="form-check-input m-0"
                                            style={{
                                                cursor: 'pointer',
                                                accentColor: '#003958'
                                            }}
                                        />
                                        <label
                                            htmlFor="checkbox-qrcode"
                                            className="flex-grow-1 mb-0"
                                            style={{ cursor: 'pointer', fontSize: '13px' }}
                                        >
                                            Exibir QR Code
                                        </label>
                                    </div>
                                </div>
                                <div className="col-md-6 col-12">
                                    <div className="d-flex align-items-center gap-2 p-2 border rounded" style={{ backgroundColor: 'white' }}>
                                        <input
                                            type="checkbox"
                                            id="checkbox-labels"
                                            checked={showLabels}
                                            onChange={(e) => setShowLabels(e.target.checked)}
                                            className="form-check-input m-0"
                                            style={{
                                                cursor: 'pointer',
                                                accentColor: '#003958'
                                            }}
                                        />
                                        <label
                                            htmlFor="checkbox-labels"
                                            className="flex-grow-1 mb-0"
                                            style={{ cursor: 'pointer', fontSize: '13px' }}
                                        >
                                            Exibir Títulos
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Controles de tamanho de fonte */}
                        <div className="mt-3 pt-2 border-top">
                            <div className="row g-2">
                                <div className="col-md-6 col-12">
                                    <div className="d-flex align-items-center gap-2 p-2 border rounded" style={{ backgroundColor: 'white' }}>
                                        <label
                                            htmlFor="input-label-fontsize"
                                            className="mb-0"
                                            style={{ fontSize: '13px', minWidth: '100px' }}
                                        >
                                            Fonte Título (px):
                                        </label>
                                        <input
                                            type="number"
                                            id="input-label-fontsize"
                                            value={labelFontSize}
                                            onChange={(e) => setLabelFontSize(parseInt(e.target.value) || 14)}
                                            className="form-control form-control-sm"
                                            min="8"
                                            max="32"
                                            style={{ maxWidth: '80px' }}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6 col-12">
                                    <div className="d-flex align-items-center gap-2 p-2 border rounded" style={{ backgroundColor: 'white' }}>
                                        <label
                                            htmlFor="input-value-fontsize"
                                            className="mb-0"
                                            style={{ fontSize: '13px', minWidth: '100px' }}
                                        >
                                            Fonte Valor (px):
                                        </label>
                                        <input
                                            type="number"
                                            id="input-value-fontsize"
                                            value={valueFontSize}
                                            onChange={(e) => setValueFontSize(parseInt(e.target.value) || 12)}
                                            className="form-control form-control-sm"
                                            min="8"
                                            max="32"
                                            style={{ maxWidth: '80px' }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {props.funcionario && (
                        <PulseiraComponent
                            codeNumber={props.funcionario.codeNumber}
                            fullName={props.funcionario.fullName}
                            cpf={props.funcionario.cpf}
                            company={company?.name || ''}
                            staff={props.funcionario.staff}
                            function={props.funcionario.function}
                            fieldsOrder={getSortedFields()}
                            showQRCode={showQRCode}
                            showLabels={showLabels}
                            labelFontSize={labelFontSize}
                            valueFontSize={valueFontSize}
                        />
                    )}
                </Modal.Body>

                <Modal.Footer className='d-flex justify-content-center'>

                    <form onSubmit={handleSubmit(handle)} style={{ border: '0' }}>
                        <div className='row flex justify-center py-2'>
                            <RadioGroup
                                row
                                aria-labelledby="demo-radio-buttons-group-label"
                                name="radio-buttons-group"
                                style={{
                                    justifyContent: 'center'
                                }}
                            >
                                <FormControlLabel
                                    {...register('choice')}
                                    value="checkin" control={<Radio />}
                                    label="Checkin"
                                />
                                <FormControlLabel
                                    label="Checkout"
                                    {...register('choice')}
                                    value="checkout"
                                    control={<Radio />}
                                />
                            </RadioGroup>

                            {errors.choice && <ErrorMessage errorMessage={errors.choice?.message} />}
                        </div>
                        <MyButton endIcon={<RxLapTimer />} type='submit'>
                            Validar Manualmente
                        </MyButton>
                    </form>
                </Modal.Footer>

            </Modal>

            <Modal show={confirmModal} onHide={() => setConfirmModal(!confirmModal)}>
                <Modal.Body className='text-center my-3 text-xl'>
                    Tem certeza que deseja sair ?
                </Modal.Body>
                <footer className='row flex'>
                    <div className='col-md-6 p-3'>
                        <MyButton onClick={() => {
                            handleClose();
                        }}>
                            Sair
                        </MyButton>
                    </div>
                    <div className='col-md-6 p-3'>
                        <Button onClick={() => {
                            setConfirmModal(false);
                        }} variant='outlined' className='w-100'>
                            Voltar ao Checkin
                        </Button>
                    </div>
                </footer>
            </Modal>
        </>
    )
}

export default ModalPrintFuncionario;